import { useCallback, useEffect, useMemo, useState } from 'react'

import { isArray, isFunction } from 'lodash'
import { useSelector } from 'react-redux'
import { Metadata, retrieveMetadata } from '../utils/http'
import { selectUser } from '../reducers/UserReducer'
import { deepFuse, isObject } from '../utils'

const Modes = Object.freeze({
  OBJECT: 0,
  ARRAY: 1
})

const useRequest = ({ request, parameters = {}, defaultData = [], defaultMetadata = new Metadata(), select, enabled = true }) => {
  const user = useSelector(selectUser)

  const [data, setData] = useState(defaultData)
  const [metadata, setMetadata] = useState(defaultMetadata)
  const [loading, setLoading] = useState(true)

  const mode = useMemo(() => isArray(parameters) ? Modes.ARRAY : Modes.OBJECT, [parameters])

  const isValid = (mode === Modes.ARRAY && parameters.every(parameter => parameter !== null)) || (mode === Modes.OBJECT && isObject(parameters))

  const fetch = useCallback((request, parameters) => {
    if (isValid) {
      setLoading(true)

      const promise = mode === Modes.ARRAY ? request(user, ...parameters) : request(user, { ...parameters })

      if (promise instanceof Promise) {
        promise.then(json => {
          const data = json?.data ?? json

          setData(isFunction(select) ? state => select(data, state) : data)

          if (json?.meta) {
            setMetadata(retrieveMetadata(json.meta))
          }

          setLoading(false)
        })
      } else {
        setLoading(false)
      }
    }
  }, [isValid, mode, user, select, setLoading, setMetadata, setData])

  const reload = useCallback((additionalParameters = {}) => {
    fetch(request, deepFuse(parameters, additionalParameters))
  }, [request, parameters, fetch])

  const changePageSize = useCallback(pageSize => {
    fetch(request, { ...parameters, pageSize })
  }, [request, parameters, fetch])

  useEffect(() => {
    if (enabled && isFunction(request)) {
      fetch(request, parameters)
    }
  }, [enabled, request, parameters, fetch])

  return { data, enabled, metadata, loading, reload, setData, changePageSize }
}

export default useRequest
